import { Button, Layout, Menu, Tooltip, Typography } from 'antd';
import {
  ClockCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDesignSystem } from '../../../app/providers/ThemeProvider';
import { useNavigationAids } from '../../../app/providers/NavigationAidsProvider';
import { useAuthSession } from '../../../app/providers/AuthSessionProvider';
import {
  getNavShowcaseId,
  getNavEntry,
  getSelectedNavKey,
  getVisibleNavEntries,
  navSectionOrder,
} from './navigationConfig';
import { useGuardedNavigate } from '../../../app/providers/FormSafetyProvider';
import { ENABLE_AUTH } from '../../utils/constants';
import { canCreateBillingDocuments } from '../../../app/router/accessControl';
import styles from './SidebarNav.module.css';

const { Sider } = Layout;
const { Text } = Typography;
const EXPANDED_WIDTHS = {
  auroraMotion: 304,
  cleanDashboard: 320,
  componentSidebar: 320,
  workspaceDark: 288,
};
const COLLAPSED_WIDTH = 112;

function SidebarLayoutFrame({ header, body, footer = null }) {
  return (
    <div className={styles.expandedLayout}>
      <div className={styles.expandedHeader}>{header}</div>
      <div className={styles.expandedBody}>
        {body}
      </div>
      {footer ? <div className={styles.expandedFooter}>{footer}</div> : null}
    </div>
  );
}

function SidebarCollapseButton({ collapsed, onToggle, rail = false }) {
  return (
    <Tooltip title={collapsed ? 'Expand navigation' : 'Compact navigation'} placement={rail ? 'right' : 'bottom'}>
      <Button
        className={`${styles.collapseButton} ${rail ? styles.collapseButtonRail : ''}`}
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={onToggle}
        aria-label={collapsed ? 'Expand navigation' : 'Compact navigation'}
        data-testid="sidebar-collapse-toggle"
      />
    </Tooltip>
  );
}

function SidebarBrand({ layoutId, rail = false, showHelperText = true }) {
  if (rail) {
    return (
      <Tooltip title="Hatched Billing" placement="right">
        <div className={styles.railBrand} role="img" aria-label="Hatched Billing">
          <span className={styles.railBrandGlow} />
          <span className={`${styles.brandMark} ${styles.railBrandMark}`}>
            <span className={styles.brandMarkText}>HB</span>
          </span>
          <span className={styles.railBrandAccent} />
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={styles.brand}>
      <span className={styles.brandMark}>
        <span className={styles.brandMarkText}>HB</span>
      </span>
      <div className={styles.brandCopy}>
        {showHelperText ? <span className={styles.brandBadge}>Workspace</span> : null}
        <Text strong className={styles.brandTitle}>
          Hatched Billing
        </Text>
      </div>
    </div>
  );
}

function SidebarQuickActions({ onNavigate, rail = false }) {
  if (rail) {
    return (
      <div className={styles.railQuickActions}>
        <Tooltip title="New Invoice" placement="right">
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={() => onNavigate('/invoices/new')}
            aria-label="New Invoice"
          />
        </Tooltip>
        <Tooltip title="New Quotation" placement="right">
          <Button
            shape="circle"
            icon={<RightOutlined />}
            onClick={() => onNavigate('/quotations/new')}
            aria-label="New Quotation"
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={styles.quickActionBlock} data-testid="sidebar-quick-actions">
      <Button type="primary" icon={<PlusOutlined />} block onClick={() => onNavigate('/invoices/new')}>
        New Invoice
      </Button>
      <Button icon={<RightOutlined />} block onClick={() => onNavigate('/quotations/new')}>
        New Quotation
      </Button>
    </div>
  );
}

function MobileSidebarBody({
  currentPath,
  favoriteEntries,
  layoutId,
  navEntries,
  onOpenCommandPalette,
  selectedKey,
  recentPages,
  showSidebarRecentPages,
  showSidebarQuickActions,
  showCreateActions,
  profileEmail,
  profileName,
  onNavigate,
}) {
  const navigationAidBlock = (
    <SidebarNavigationAids
      currentPath={currentPath}
      favoriteEntries={favoriteEntries}
      navEntries={navEntries}
      recentPages={recentPages}
      onNavigate={onNavigate}
      showHelperText={false}
      showSidebarRecentPages={showSidebarRecentPages}
    />
  );

  return (
    <div className={styles.mobileLayout}>
      <div className={styles.mobileHeader}>
        <SidebarBrand layoutId={layoutId} showHelperText={false} />
        <button
          type="button"
          className={styles.searchInput}
          aria-label="Open sidebar search"
          onClick={onOpenCommandPalette}
          data-showcase-id="sidebar-search"
        >
          <span className={styles.searchInputIcon}>
            <SearchOutlined />
          </span>
          <span className={styles.searchInputText}>Search pages</span>
        </button>
        {showSidebarQuickActions && showCreateActions ? (
          <div className={styles.mobileQuickRegion}>
            <SidebarQuickActions onNavigate={onNavigate} />
          </div>
        ) : null}
      </div>

      <div className={styles.primaryScrollRegion} data-testid="sidebar-scroll-region">
        {navigationAidBlock}
        <MenuBlock
          items={buildSectionedItems(navEntries, navSectionOrder, false)}
          selectedKey={selectedKey}
          onNavigate={onNavigate}
          className={styles.menuMobile}
        />
      </div>

      <div className={styles.mobileFooter}>
        <ProfileCard
          dark={layoutId === 'auroraMotion' || layoutId === 'workspaceDark'}
          name={profileName}
          email={profileEmail}
        />
      </div>
    </div>
  );
}

function ProfileCard({
  dark = false,
  name = 'Hatched Team',
  email = 'hello@hatchedbilling.app',
}) {
  const avatarLabel = String(name || 'H')
    .trim()
    .charAt(0)
    .toUpperCase() || 'H';

  return (
    <div className={`${styles.profileCard} ${dark ? styles.profileCardDark : ''}`}>
      <span className={styles.profileAvatar}>{avatarLabel}</span>
      <div className={styles.profileCopy}>
        <span className={styles.profileName}>{name}</span>
        <span className={styles.profileEmail}>{email}</span>
      </div>
    </div>
  );
}

function PromoCard({ showHelperText = true }) {
  return (
    <div className={styles.promoCard}>
      <span className={styles.promoEyebrow}>Workspace</span>
      <strong className={styles.promoTitle}>Fast billing lane</strong>
      {showHelperText ? (
        <span className={styles.promoHint}>Keep key billing flows one click away.</span>
      ) : null}
      <span className={styles.promoPill}>Shared shell pattern</span>
    </div>
  );
}

function SpotlightCard({ selectedEntry, showHelperText = true }) {
  return (
    <div className={styles.spotlightCard}>
      <span className={styles.spotlightEyebrow}>Active page</span>
      <strong className={styles.spotlightTitle}>{selectedEntry.title}</strong>
      {showHelperText ? <span className={styles.spotlightHint}>{selectedEntry.hint}</span> : null}
    </div>
  );
}

function buildSectionedItems(entries, sectionNames = navSectionOrder, showHelperText = true) {
  return sectionNames
    .map((section) => {
      const children = entries
        .filter((entry) => entry.section === section)
        .map((entry) => {
          const Icon = entry.icon;

          return {
            key: entry.key,
            label: (
              <span className={styles.navLink} data-showcase-id={getNavShowcaseId(entry.key)}>
                <span className={styles.navIcon}>
                  <Icon />
                </span>
                <span className={styles.navText}>
                  <span className={styles.navTitle}>{entry.title}</span>
                  {showHelperText ? <span className={styles.navHint}>{entry.hint}</span> : null}
                </span>
              </span>
            ),
          };
        });

      if (!children.length) {
        return null;
      }

      return {
        type: 'group',
        key: section,
        label: <span className={styles.groupLabel}>{section}</span>,
        children,
      };
    })
    .filter(Boolean);
}

function MenuBlock({ items, selectedKey, onNavigate, className = '' }) {
  return (
    <Menu
      mode="inline"
      items={items}
      selectedKeys={[selectedKey]}
      className={`${styles.menu} ${className}`.trim()}
      onClick={({ key }) => onNavigate(key)}
    />
  );
}

function RailNavigation({ entries, selectedKey, onNavigate }) {
  return (
    <nav className={styles.railMenu} aria-label="Primary navigation">
      {entries.map((entry) => {
        const Icon = entry.icon;
        const active = entry.key === selectedKey;

        return (
          <Tooltip key={entry.key} title={entry.title} placement="right">
            <button
              type="button"
              className={`${styles.railItem} ${active ? styles.railItemActive : ''}`}
              onClick={() => onNavigate(entry.key)}
              aria-label={entry.title}
              data-showcase-id={getNavShowcaseId(entry.key)}
            >
              <Icon />
            </button>
          </Tooltip>
        );
      })}
    </nav>
  );
}

function NavigationShortcutCard({ title, entries, onNavigate, showHelperText = true }) {
  if (!entries.length) {
    return null;
  }

  return (
    <div className={styles.shortcutCard} data-testid={`sidebar-shortcut-${title.toLowerCase()}`}>
      <div className={styles.shortcutHeader}>
        <span className={styles.shortcutEyebrow}>{title}</span>
        <span className={styles.shortcutCount}>{entries.length}</span>
      </div>
      <div className={styles.shortcutList}>
        {entries.map((entry) => {
          const Icon = entry.icon;

          return (
            <button
              key={`${title}-${entry.path}`}
              type="button"
              className={styles.shortcutItem}
              onClick={() => onNavigate(entry.path)}
            >
              <span className={styles.shortcutIcon}>
                <Icon />
              </span>
              <span className={styles.shortcutCopy}>
                <span className={styles.shortcutTitle}>{entry.label}</span>
                {showHelperText && entry.hint ? (
                  <span className={styles.shortcutHint}>{entry.hint}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SidebarNavigationAids({
  currentPath,
  favoriteEntries,
  navEntries,
  recentPages,
  onNavigate,
  showHelperText,
  showSidebarRecentPages,
}) {
  const favoriteItems = favoriteEntries.slice(0, 4).map((entry) => ({
    path: entry.key,
    label: entry.title,
    hint: entry.hint,
    icon: entry.icon || StarFilled,
  }));
  const recentItems = showSidebarRecentPages
    ? recentPages
        .filter((item) => item.path !== currentPath)
        .slice(0, 3)
        .map((item) => ({
          path: item.path,
          label: item.title,
          hint: item.hint || item.section,
          icon: navEntries.find((entry) => entry.key === item.navKey)?.icon || ClockCircleOutlined,
        }))
    : [];

  if (!favoriteItems.length && !recentItems.length) {
    return null;
  }

  return (
    <div className={styles.navigationAidStack} data-testid="sidebar-navigation-aids">
      <NavigationShortcutCard
        title="Favorites"
        entries={favoriteItems}
        onNavigate={onNavigate}
        showHelperText={showHelperText}
      />
      <NavigationShortcutCard
        title="Recent"
        entries={recentItems}
        onNavigate={onNavigate}
        showHelperText={showHelperText}
      />
    </div>
  );
}

function ExpandedSidebarBody({
  currentPath,
  favoriteEntries,
  layoutId,
  navEntries,
  onOpenCommandPalette,
  selectedEntry,
  selectedKey,
  recentPages,
  showSidebarRecentPages,
  showSidebarQuickActions,
  showCreateActions,
  showHelperText,
  profileEmail,
  profileName,
  onNavigate,
  onToggleCollapsed,
}) {
  const mainItems = buildSectionedItems(navEntries, navSectionOrder, showHelperText);
  const navigationAidBlock = (
    <SidebarNavigationAids
      currentPath={currentPath}
      favoriteEntries={favoriteEntries}
      navEntries={navEntries}
      recentPages={recentPages}
      onNavigate={onNavigate}
      showHelperText={showHelperText}
      showSidebarRecentPages={showSidebarRecentPages}
    />
  );

  if (layoutId === 'auroraMotion') {
    return (
      <SidebarLayoutFrame
        header={(
          <>
            <div className={styles.brandRow}>
              <SidebarBrand layoutId={layoutId} showHelperText={showHelperText} />
              <SidebarCollapseButton collapsed={false} onToggle={onToggleCollapsed} />
            </div>
            <SpotlightCard selectedEntry={selectedEntry} showHelperText={showHelperText} />
            {showSidebarQuickActions && showCreateActions ? (
              <SidebarQuickActions onNavigate={onNavigate} />
            ) : null}
          </>
        )}
        body={(
          <div className={styles.primaryScrollRegion} data-testid="sidebar-scroll-region">
            {navigationAidBlock}
            <MenuBlock
              items={mainItems}
              selectedKey={selectedKey}
              onNavigate={onNavigate}
              className={styles.menuAurora}
            />
          </div>
        )}
        footer={<ProfileCard dark name={profileName} email={profileEmail} />}
      />
    );
  }

  if (layoutId === 'cleanDashboard') {
    return (
      <SidebarLayoutFrame
        header={(
          <>
            <div className={styles.brandRow}>
              <SidebarBrand layoutId={layoutId} showHelperText={showHelperText} />
              <SidebarCollapseButton collapsed={false} onToggle={onToggleCollapsed} />
            </div>
            {showHelperText ? (
              <div className={styles.metaStrip}>
                <span className={styles.metaPill}>Company</span>
                <span className={styles.metaPill}>Active workspace</span>
              </div>
            ) : null}
          </>
        )}
        body={(
          <div className={styles.primaryScrollRegion} data-testid="sidebar-scroll-region">
            {navigationAidBlock}
            <MenuBlock
              items={mainItems}
              selectedKey={selectedKey}
              onNavigate={onNavigate}
              className={styles.menuClean}
            />
          </div>
        )}
        footer={(
          <>
            {showSidebarQuickActions && showCreateActions ? <SidebarQuickActions onNavigate={onNavigate} /> : null}
            <ProfileCard name={profileName} email={profileEmail} />
          </>
        )}
      />
    );
  }

  if (layoutId === 'componentSidebar') {
    return (
      <SidebarLayoutFrame
        header={(
          <>
            <div className={styles.brandRow}>
              <SidebarBrand layoutId={layoutId} showHelperText={showHelperText} />
              <SidebarCollapseButton collapsed={false} onToggle={onToggleCollapsed} />
            </div>
            {showSidebarQuickActions && showCreateActions ? (
              <SidebarQuickActions onNavigate={onNavigate} />
            ) : null}
          </>
        )}
        body={(
          <div
            className={`${styles.sidebarStack} ${styles.primaryScrollRegion}`}
            data-testid="sidebar-scroll-region"
          >
            {navigationAidBlock}
            <MenuBlock
              items={mainItems}
              selectedKey={selectedKey}
              onNavigate={onNavigate}
              className={styles.menuComponent}
            />
            <PromoCard showHelperText={showHelperText} />
          </div>
        )}
        footer={<ProfileCard name={profileName} email={profileEmail} />}
      />
    );
  }

  return (
    <SidebarLayoutFrame
      header={(
        <>
          <div className={styles.brandRow}>
            <SidebarBrand layoutId={layoutId} showHelperText={showHelperText} />
            <SidebarCollapseButton collapsed={false} onToggle={onToggleCollapsed} />
          </div>
          <button
            type="button"
            className={styles.searchInput}
            aria-label="Open sidebar search"
            title="Open command palette (Ctrl/Cmd + K)"
            onClick={onOpenCommandPalette}
            data-showcase-id="sidebar-search"
          >
            <span className={styles.searchInputIcon}>
              <SearchOutlined />
            </span>
            <span className={styles.searchInputText}>Search pages</span>
          </button>
          {showSidebarQuickActions && showCreateActions ? <SidebarQuickActions onNavigate={onNavigate} /> : null}
        </>
      )}
      body={(
        <div className={styles.primaryScrollRegion} data-testid="sidebar-scroll-region">
          {navigationAidBlock}
          <MenuBlock
            items={mainItems}
            selectedKey={selectedKey}
            onNavigate={onNavigate}
            className={styles.menuDark}
          />
        </div>
      )}
      footer={<ProfileCard dark name={profileName} email={profileEmail} />}
    />
  );
}

export default function SidebarNav({ mobile = false, onNavigate = null }) {
  const location = useLocation();
  const guardedNavigate = useGuardedNavigate();
  const { activeNavigationLayout, setUiCustomization, showHelperText, uiCustomization } = useDesignSystem();
  const { favoriteEntries, openCommandPalette, recentPages } = useNavigationAids();
  const { session, isReady: isAuthReady = true } = useAuthSession();

  const visibleNavEntries = useMemo(
    () =>
      getVisibleNavEntries(session?.role, {
        enableAuth: ENABLE_AUTH,
        permissions: session?.permissions,
        authResolved: isAuthReady,
      }),
    [isAuthReady, session?.permissions, session?.role]
  );
  const roleBasedFavorites = useMemo(
    () => favoriteEntries.filter((entry) => visibleNavEntries.some((navEntry) => navEntry.key === entry.key)),
    [favoriteEntries, visibleNavEntries]
  );
  const showCreateActions = useMemo(() => {
    return canCreateBillingDocuments(session?.role, { enableAuth: ENABLE_AUTH });
  }, [session?.role]);
  const profileName = session?.user?.fullName || 'Hatched Team';
  const profileEmail = session?.user?.email || 'hello@hatchedbilling.app';

  const selectedKey = getSelectedNavKey(location.pathname, visibleNavEntries);
  const selectedEntry = getNavEntry(location.pathname, visibleNavEntries);
  const layoutId = activeNavigationLayout.id;
  const sidebarCollapsed = uiCustomization.sidebarCollapsed;
  const showRailLayout = sidebarCollapsed && !mobile;

  const handleNavigate = (path) => {
    const didNavigate = guardedNavigate(path);
    if (didNavigate && onNavigate) {
      onNavigate();
    }
  };

  const toggleSidebarCollapsed = () => {
    setUiCustomization((currentValue) => ({
      sidebarCollapsed: !currentValue.sidebarCollapsed,
    }));
  };

  const navBody = showRailLayout ? (
    <div className={styles.railLayout}>
      <div className={styles.railHeader}>
        <SidebarCollapseButton collapsed={sidebarCollapsed} onToggle={toggleSidebarCollapsed} rail />
        <SidebarBrand layoutId={layoutId} rail showHelperText={showHelperText} />
      </div>
      <RailNavigation entries={visibleNavEntries} selectedKey={selectedKey} onNavigate={handleNavigate} />
    </div>
  ) : mobile ? (
    <MobileSidebarBody
      currentPath={location.pathname}
      favoriteEntries={roleBasedFavorites}
      layoutId={layoutId}
      navEntries={visibleNavEntries}
      onOpenCommandPalette={openCommandPalette}
      selectedKey={selectedKey}
      recentPages={recentPages}
      showSidebarRecentPages={uiCustomization.showSidebarRecentPages}
      showSidebarQuickActions={uiCustomization.showSidebarQuickActions}
      showCreateActions={showCreateActions}
      profileName={profileName}
      profileEmail={profileEmail}
      onNavigate={handleNavigate}
    />
  ) : (
    <ExpandedSidebarBody
      currentPath={location.pathname}
      favoriteEntries={roleBasedFavorites}
      layoutId={layoutId}
      navEntries={visibleNavEntries}
      onOpenCommandPalette={openCommandPalette}
      selectedEntry={selectedEntry}
      selectedKey={selectedKey}
      recentPages={recentPages}
      showSidebarRecentPages={uiCustomization.showSidebarRecentPages}
      showSidebarQuickActions={uiCustomization.showSidebarQuickActions}
      showCreateActions={showCreateActions}
      showHelperText={showHelperText}
      profileName={profileName}
      profileEmail={profileEmail}
      onNavigate={handleNavigate}
      onToggleCollapsed={toggleSidebarCollapsed}
    />
  );

  const frameClassName = [
    styles.siderInner,
    mobile ? styles.mobileFrame : '',
    styles[`frame${layoutId}`],
    showRailLayout ? styles.frameRail : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (mobile) {
    return (
      <div
        className={frameClassName}
        data-testid="sidebar-frame"
        data-navigation-layout={layoutId}
        data-collapsed="false"
      >
        {navBody}
      </div>
    );
  }

  return (
    <Sider
      width={showRailLayout ? COLLAPSED_WIDTH : EXPANDED_WIDTHS[layoutId]}
      className={styles.sider}
      data-testid="sidebar-sider"
    >
      <div
        className={frameClassName}
        data-testid="sidebar-frame"
        data-navigation-layout={layoutId}
        data-collapsed={String(showRailLayout)}
      >
        {navBody}
      </div>
    </Sider>
  );
}
